import { useContext, useEffect, useState } from 'react'
import { ExclamationCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { Flex, Form, Modal, Typography } from 'antd'
import {
  CapitalHarnessContext,
  SHOW_CAPITAL_HARNESS_CLONE_POPUP,
} from '../context/context'
import { NavContext } from '../../../../context/global_context'
import { useParams } from 'react-router-dom'
import SelectInput from '../../../common/SelectInput'
import { getRequest } from '../../../../services/base'
import Button from '../../../common/Button'
import { downloadFile } from '../../../../utils/common'
import useDebouncedValue from '../../../../utils/common_function'
import { useIntl } from 'react-intl'

const CloneDataFromPopup = () => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [modal, contextHolder] = Modal.useModal()
  const [state, dispatch] = useContext(CapitalHarnessContext)
  const [, navDispatch] = useContext(NavContext)
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [searchFailed, setSearchFailed] = useState(false)
  const [result, setResult] = useState({})
  const [showMessage, setShowMessage] = useState(
    intl.formatMessage({ id: 'capitalHarness.selectVehicleMessage' })
  )
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 500)

  useEffect(() => {
    getVehicleList('')
  }, [])

  useEffect(() => {
    if (debouncedSearch !== undefined) getVehicleList(debouncedSearch)
  }, [debouncedSearch])

  const getVehicleList = async (search = '') => {
    let res = await getRequest(`/api/vehicles/?limit=50&search=${search}`)
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setVehicleOptions([])
      return
    }
    setSearchFailed(false)
    setVehicleOptions(
      (res?.results || []).map((item) => ({
        ...item,
        label: item.name,
        value: item.vehicle_id,
      }))
    )
  }

  const getMappingData = async (value) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'capitalHarness.cloneConfirmationTitle' }),
      content: intl.formatMessage(
        { id: 'capitalHarness.cloneConfirmationContent' },
        { name: state?.selected_type?.name, vehicleId: value.vehicle_id }
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.confirm' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        setShowMessage(
          intl.formatMessage({ id: 'capitalHarness.loadingData' })
        )
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage(
              { id: 'capitalHarness.copyingData' },
              { vehicleId: value.vehicle_id }
            ),
            showLoader: true,
          },
          notification: {},
        })
        let res = await getRequest(
          `/api/plugin/capital_harness/fetch_related_data/${value.vehicle_id}/?selectedId=${state?.selected_object?.id}&selectedType=${state?.selected_type?.value}`
        )
        if (res?.status) {
          setResult(res?.data)
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'capitalHarness.copyCompleted' }),
            },
          })
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'capitalHarness.cloneFailed' }),
            },
          })
        }
        setShowMessage('')
      },
    })
  }

  const downloadMissingFile = async (fileType, fileName) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage(
          { id: 'capitalHarness.downloadingMissingFile' },
          { fileName }
        ),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/plugin/capital_harness/download_file/?selectedId=${state?.selected_object?.id}&fileType=${fileType}`
    )
    // `res?.data?.missing_file` doubled as the failure test; make it explicit so
    // a failed request is distinguishable from a response with no file.
    if (res?.status === false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'capitalHarness.downloadMissingError' }),
        },
      })
      return
    }
    if (res?.data?.missing_file) {
      const saved = await downloadFile(res?.data?.missing_file, fileName)
      if (!saved) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'capitalHarness.downloadMissingError' }),
          },
        })
        return
      }
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage(
            { id: 'capitalHarness.fileDownloaded' },
            { fileName }
          ),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'capitalHarness.downloadMissingError' }),
        },
      })
    }
  }

  const closeModal = () => {
    dispatch({
      type: SHOW_CAPITAL_HARNESS_CLONE_POPUP,
      data: {},
      showClonePopup: false,
      selectedType: {},
    })
    setResult({})
    setShowMessage(intl.formatMessage({ id: 'capitalHarness.selectVehicleMessage' }))
  }

  return (
    <Modal
      title={
        <Flex align="center" justify="space-between" gap={16}>
          <Typography.Text strong>{state?.selected_object?.name}</Typography.Text>
        </Flex>
      }
      open={state?.show_clone_popup}
      centered={true}
      onCancel={closeModal}
      width="75%"
      footer={null}
      className="capital-harness-clone-modal"
      destroyOnHidden
    >
      <Form layout="vertical">
        <SelectInput
          notFoundContent={
            searchFailed ? (
              <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                {intl.formatMessage({ id: 'generic.searchFailed' })}
              </Typography.Text>
            ) : undefined
          }
          name="vehicle"
          label={intl.formatMessage(
            { id: 'capitalHarness.cloneVehicleLabel' },
            { name: state?.selected_type?.name }
          )}
          options={vehicleOptions}
          placeholder={intl.formatMessage({ id: 'capitalHarness.selectVehiclePlaceholder' })}
          isRequired={false}
          showSearch
          allowClear
          onInputChange={(value) => setSearch(value || '')}
          onChange={(value) => {
            if (value) getMappingData(value)
          }}
          formatLabel={(option) => option?.label || option?.vehicle_id}
        />
      </Form>

      {showMessage && (
        <Typography.Paragraph style={{ textAlign: 'center', marginTop: 16, marginBottom: 8 }}>
          {showMessage}
        </Typography.Paragraph>
      )}

      {/* Gate on `status` rather than `msg`: the backend sends both on every
          clone response, and `result` starts as {} so it cannot gate itself. */}
      {result?.status !== undefined && (
        <Typography.Paragraph
          style={{ textAlign: 'center', marginBottom: 8 }}
          type={result?.status ? 'success' : 'danger'}
        >
          {intl.formatMessage({
            id: result?.status ? 'capitalHarness.copyCompleted' : 'capitalHarness.cloneFailed',
          })}
        </Typography.Paragraph>
      )}

      {(result?.wire?.missing?.length > 0 ||
        result?.wire?.mapped?.length > 0 ||
        result?.connector?.missing?.length > 0 ||
        result?.connector?.mapped?.length > 0) && (
        <Flex vertical gap={8} className="clone-summary" style={{ marginTop: 8 }}>
          {result?.wire?.missing?.length > 0 && (
            <Flex align="center" justify="space-between" gap={16}>
              <Typography.Text className='bolder'>
                {intl.formatMessage(
                  { id: 'capitalHarness.missingCircuits' },
                  { count: result.wire.missing.length }
                )}
              </Typography.Text>
              <Button
                type="link"
                className="link-button"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadMissingFile('0', `${vehicle}-missing-circuit.csv`)
                }
              >
                {intl.formatMessage({ id: 'capitalHarness.downloadMissingCircuits' })}
              </Button>
            </Flex>
          )}
          {result?.wire?.mapped?.length > 0 && (
            <Typography.Text>
              {intl.formatMessage(
                { id: 'capitalHarness.mappedCircuits' },
                { count: result.wire.mapped.length }
              )}
            </Typography.Text>
          )}
          {result?.connector?.missing?.length > 0 && (
            <Flex align="center" justify="space-between" gap={16}>
              <Typography.Text className='bolder'>
                {intl.formatMessage(
                  { id: 'capitalHarness.missingDevices' },
                  { count: result.connector.missing.length }
                )}
              </Typography.Text>
              <Button
                type="link"
                className="link-button"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadMissingFile('1', `${vehicle}-missing-devices.csv`)
                }
              >
                {intl.formatMessage({ id: 'capitalHarness.downloadMissingDevices' })}
              </Button>
            </Flex>
          )}
          {result?.connector?.mapped?.length > 0 && (
            <Typography.Text>
              {intl.formatMessage(
                { id: 'capitalHarness.mappedDevices' },
                { count: result.connector.mapped.length }
              )}
            </Typography.Text>
          )}
        </Flex>
      )}

      <Flex justify="end" style={{ marginTop: 16 }}>
        <Button onClick={closeModal}>
          {intl.formatMessage({ id: 'forms.close' })}
        </Button>
      </Flex>

      {contextHolder}
    </Modal>
  )
}

export default CloneDataFromPopup
